import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { v4 as uuidv4 } from 'uuid';
import { BuilderState } from '../types/builder';
import { WebsiteConfig, Block } from '../types/websiteConfig';
import { websiteConfigService } from '../services/websiteConfigService';
import { websiteTemplateService } from '../services/websiteTemplateService';
import { MAX_HISTORY_ITEMS } from '../constants/config';

export const useWebsiteBuilder = create<BuilderState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        config: null,
        selectedBlockId: null,
        hoveredBlockId: null,
        draggedBlock: null,
        viewport: 'desktop',
        zoom: 100,
        leftPanelOpen: true,
        rightPanelOpen: true,
        activeRightTab: 'settings',
        history: [],
        historyIndex: -1,
        maxHistory: MAX_HISTORY_ITEMS,
        isDirty: false,
        isSaving: false,
        isPreview: false,
        lastSaved: null,
        isNewConfig: true, // Start as true, will be set to false when loading existing config
        isLoading: false,
        isPublishing: false,
        errors: [],
        // Template mode state (for SUPER_ADMIN)
        isTemplateMode: false,
        selectedShopType: null,

        // Load configuration
        loadConfig: async (shopId: string) => {
          console.log('📥 [Store] Loading config for shop:', shopId);
          set({ isLoading: true, errors: [] });
          try {
            const config = await websiteConfigService.getConfig(shopId);
            console.log('✅ [Store] Config loaded successfully:', config);

            // Check if this is a default template (defaultTemplate: true) or actual shop config
            const isDefaultTemplate = (config as any).defaultTemplate === true;

            set({
              config,
              history: [config],
              historyIndex: 0,
              isLoading: false,
              isDirty: isDefaultTemplate, // Mark as dirty if it's a default template that needs to be saved
              isNewConfig: isDefaultTemplate, // true if defaultTemplate=true (use POST), false otherwise (use PUT)
              lastSaved: config.createdAt && !isDefaultTemplate ? new Date(config.createdAt) : null
            });
          } catch (error: any) {
            console.error('❌ [Store] Failed to load config:', error);
            set({
              isLoading: false,
              errors: [{
                id: uuidv4(),
                message: error.message || 'Failed to load website configuration',
                type: 'error'
              }]
            });
          }
        },

        // Add block
        addBlock: (block: Block, position: number) => {
          const { config, history, historyIndex } = get();
          if (!config) return;

          console.log('➕ [Store] Adding block:', block.type, 'at position:', position);

          const newBlocks = [...config.blocks];
          newBlocks.splice(position, 0, block);

          // Update order for all blocks
          newBlocks.forEach((b, i) => b.order = i);

          const newConfig: WebsiteConfig = {
            ...config,
            blocks: newBlocks,
            updatedAt: new Date().toISOString()
          };

          const newHistory = [...history.slice(0, historyIndex + 1), newConfig];
          if (newHistory.length > MAX_HISTORY_ITEMS) {
            newHistory.shift();
          } else {
            set({ historyIndex: historyIndex + 1 });
          }

          set({
            config: newConfig,
            history: newHistory,
            isDirty: true,
            selectedBlockId: block.id
          });
        },

        // Update block
        updateBlock: (blockId: string, updates: Partial<Block>) => {
          const { config, history, historyIndex } = get();
          if (!config) return;

          console.log('✏️ [Store] Updating block:', blockId, 'with updates:', updates);

          const newBlocks = config.blocks.map(b => {
            if (b.id === blockId) {
              console.log('✏️ [Store] Found block. Current config:', b.config);
              // Deep merge config if it exists in updates
              if (updates.config) {
                const updatedBlock = {
                  ...b,
                  ...updates,
                  config: {
                    ...b.config,
                    ...updates.config
                  }
                };
                console.log('✏️ [Store] Updated block config:', updatedBlock.config);
                return updatedBlock;
              }
              return { ...b, ...updates };
            }
            return b;
          });

          const newConfig: WebsiteConfig = {
            ...config,
            blocks: newBlocks,
            updatedAt: new Date().toISOString()
          };

          console.log('✏️ [Store] New config created with updated blocks');

          const newHistory = [...history.slice(0, historyIndex + 1), newConfig];
          if (newHistory.length > MAX_HISTORY_ITEMS) {
            newHistory.shift();
          } else {
            set({ historyIndex: historyIndex + 1 });
          }

          set({
            config: newConfig,
            history: newHistory,
            isDirty: true
          });
        },

        // Remove block
        removeBlock: (blockId: string) => {
          const { config, history, historyIndex } = get();
          if (!config) return;

          console.log('🗑️ [Store] Removing block:', blockId);

          const newBlocks = config.blocks
            .filter(b => b.id !== blockId)
            .map((b, i) => ({ ...b, order: i }));

          const newConfig: WebsiteConfig = {
            ...config,
            blocks: newBlocks,
            updatedAt: new Date().toISOString()
          };

          const newHistory = [...history.slice(0, historyIndex + 1), newConfig];
          if (newHistory.length > MAX_HISTORY_ITEMS) {
            newHistory.shift();
          } else {
            set({ historyIndex: historyIndex + 1 });
          }

          set({
            config: newConfig,
            history: newHistory,
            isDirty: true,
            selectedBlockId: null
          });
        },

        // Reorder blocks
        reorderBlocks: (sourceIndex: number, destIndex: number) => {
          const { config, history, historyIndex } = get();
          if (!config) return;

          console.log('🔄 [Store] Reordering blocks:', sourceIndex, '->', destIndex);

          const newBlocks = [...config.blocks];
          const [removed] = newBlocks.splice(sourceIndex, 1);
          newBlocks.splice(destIndex, 0, removed);

          // Update order
          newBlocks.forEach((b, i) => b.order = i);

          const newConfig: WebsiteConfig = {
            ...config,
            blocks: newBlocks,
            updatedAt: new Date().toISOString()
          };

          const newHistory = [...history.slice(0, historyIndex + 1), newConfig];
          if (newHistory.length > MAX_HISTORY_ITEMS) {
            newHistory.shift();
          } else {
            set({ historyIndex: historyIndex + 1 });
          }

          set({
            config: newConfig,
            history: newHistory,
            isDirty: true
          });
        },

        // Update theme
        updateTheme: (themeUpdates) => {
          const { config, history, historyIndex } = get();
          if (!config) return;

          console.log('🎨 [Store] Updating theme:', themeUpdates);

          const newConfig: WebsiteConfig = {
            ...config,
            theme: {
              ...config.theme,
              ...themeUpdates
            },
            updatedAt: new Date().toISOString()
          };

          console.log('🎨 [Store] New theme config:', newConfig.theme);

          const newHistory = [...history.slice(0, historyIndex + 1), newConfig];
          if (newHistory.length > MAX_HISTORY_ITEMS) {
            newHistory.shift();
          } else {
            set({ historyIndex: historyIndex + 1 });
          }

          set({
            config: newConfig,
            history: newHistory,
            isDirty: true
          });
        },

        // Update header
        updateHeader: (headerUpdates) => {
          const { config, history, historyIndex } = get();
          if (!config) return;

          const newConfig: WebsiteConfig = {
            ...config,
            header: { ...config.header, ...headerUpdates },
            updatedAt: new Date().toISOString()
          };

          const newHistory = [...history.slice(0, historyIndex + 1), newConfig];
          if (newHistory.length > MAX_HISTORY_ITEMS) {
            newHistory.shift();
          } else {
            set({ historyIndex: historyIndex + 1 });
          }

          set({
            config: newConfig,
            history: newHistory,
            isDirty: true
          });
        },

        // Update footer
        updateFooter: (footerUpdates) => {
          const { config, history, historyIndex } = get();
          if (!config) return;

          const newConfig: WebsiteConfig = {
            ...config,
            footer: { ...config.footer, ...footerUpdates },
            updatedAt: new Date().toISOString()
          };

          const newHistory = [...history.slice(0, historyIndex + 1), newConfig];
          if (newHistory.length > MAX_HISTORY_ITEMS) {
            newHistory.shift();
          } else {
            set({ historyIndex: historyIndex + 1 });
          }

          set({
            config: newConfig,
            history: newHistory,
            isDirty: true
          });
        },

        // Update SEO
        updateSEO: (seoUpdates) => {
          const { config, history, historyIndex } = get();
          if (!config) return;

          const newConfig: WebsiteConfig = {
            ...config,
            seo: { ...config.seo, ...seoUpdates },
            updatedAt: new Date().toISOString()
          };

          const newHistory = [...history.slice(0, historyIndex + 1), newConfig];
          if (newHistory.length > MAX_HISTORY_ITEMS) {
            newHistory.shift();
          } else {
            set({ historyIndex: historyIndex + 1 });
          }

          set({
            config: newConfig,
            history: newHistory,
            isDirty: true
          });
        },

        // Undo
        undo: () => {
          const { history, historyIndex } = get();
          if (historyIndex > 0) {
            console.log('↶ [Store] Undo');
            set({
              config: history[historyIndex - 1],
              historyIndex: historyIndex - 1,
              isDirty: true
            });
          }
        },

        // Redo
        redo: () => {
          const { history, historyIndex } = get();
          if (historyIndex < history.length - 1) {
            console.log('↷ [Store] Redo');
            set({
              config: history[historyIndex + 1],
              historyIndex: historyIndex + 1,
              isDirty: true
            });
          }
        },

        // Save
        save: async () => {
          const { config, isNewConfig } = get();
          if (!config) return;

          console.log(`💾 [Store] ${isNewConfig ? 'Creating new' : 'Updating existing'} configuration`);
          set({ isSaving: true });
          try {
            // Use POST for new configs, PUT for existing ones
            const saved = isNewConfig
              ? await websiteConfigService.saveConfig(config)
              : await websiteConfigService.updateConfig(config);

            set({
              config: saved,
              isSaving: false,
              isDirty: false,
              isNewConfig: false, // After first save, it becomes an existing config
              lastSaved: new Date()
            });
            console.log(`✅ [Store] Configuration ${isNewConfig ? 'created' : 'updated'} successfully`);
          } catch (error) {
            console.error('❌ [Store] Failed to save:', error);
            set({
              isSaving: false,
              errors: [{
                id: uuidv4(),
                message: `Failed to ${isNewConfig ? 'create' : 'update'} configuration`,
                type: 'error'
              }]
            });
          }
        },

        // Publish
        publish: async () => {
          const { config, save } = get();
          if (!config) return;

          console.log('🚀 [Store] Publishing website');

          // Save first
          await save();

          set({ isPublishing: true });
          try {
            const result = await websiteConfigService.publish(config.shopId);
            set({
              isPublishing: false,
              isDirty: false
            });
            console.log('✅ [Store] Website published successfully:', result.liveUrl);
            alert(`Website published successfully!\nLive at: ${result.liveUrl}`);
          } catch (error) {
            console.error('❌ [Store] Failed to publish:', error);
            set({
              isPublishing: false,
              errors: [{
                id: uuidv4(),
                message: 'Failed to publish website',
                type: 'error'
              }]
            });
          }
        },

        // Template Mode Actions (SUPER_ADMIN only)
        setTemplateMode: (isTemplateMode: boolean) => {
          console.log('🔧 [Store] Setting template mode:', isTemplateMode);
          set({ isTemplateMode });
        },

        setSelectedShopType: (shopType: string | null) => {
          console.log('🏪 [Store] Setting selected shop type:', shopType);
          set({ selectedShopType: shopType });
        },

        loadTemplate: async (shopType: string) => {
          console.log('📥 [Store] Loading template for shop type:', shopType);
          set({ isLoading: true, errors: [], selectedShopType: shopType });
          try {
            const config = await websiteTemplateService.getTemplate(shopType);
            console.log('✅ [Store] Template loaded successfully:', config);

            // Validate config structure
            if (!config || typeof config !== 'object') {
              throw new Error('Invalid template structure: config is not an object');
            }

            // Ensure blocks array exists
            if (!config.blocks) {
              console.warn('⚠️ [Store] Template has no blocks, initializing empty array');
              config.blocks = [];
            }

            // Template exists, so it's not a new config
            set({
              config,
              history: [config],
              historyIndex: 0,
              isLoading: false,
              isDirty: false,
              isNewConfig: false, // Existing template
              lastSaved: config.updatedAt ? new Date(config.updatedAt) : new Date()
            });
          } catch (error: any) {
            console.error('❌ [Store] Failed to load template:', error);

            // If template doesn't exist, create a default one
            if (error.response?.status === 404) {
              console.log('⚠️ [Store] Template not found, creating default for:', shopType);
              const defaultConfig = websiteConfigService.createDefaultConfig(shopType);
              set({
                config: defaultConfig,
                history: [defaultConfig],
                historyIndex: 0,
                isLoading: false,
                isDirty: true,
                isNewConfig: true, // New template
                lastSaved: null
              });
            } else {
              set({
                isLoading: false,
                errors: [{
                  id: uuidv4(),
                  message: error.message || 'Failed to load template',
                  type: 'error'
                }]
              });
            }
          }
        },

        saveTemplate: async (shopType: string) => {
          const { config } = get();
          if (!config) {
            console.error('❌ [Store] Cannot save template: No config loaded');
            return;
          }

          console.log('💾 [Store] Saving template for shop type:', shopType);
          set({ isSaving: true, errors: [] });

          try {
            await websiteTemplateService.saveTemplate(shopType, config);
            set({
              isSaving: false,
              isDirty: false,
              isNewConfig: false, // After saving, it's no longer new
              lastSaved: new Date()
            });
            console.log('✅ [Store] Template saved successfully');
            alert(`Template for ${shopType} saved successfully!`);
          } catch (error: any) {
            console.error('❌ [Store] Failed to save template:', error);
            set({
              isSaving: false,
              errors: [{
                id: uuidv4(),
                message: error.message || 'Failed to save template',
                type: 'error'
              }]
            });
          }
        },

        // UI Actions
        setViewport: (viewport) => set({ viewport }),
        setZoom: (zoom) => set({ zoom }),
        setSelectedBlock: (blockId) => set({ selectedBlockId: blockId }),
        setHoveredBlock: (blockId) => set({ hoveredBlockId: blockId }),
        setDraggedBlock: (block) => set({ draggedBlock: block }),
        toggleLeftPanel: () => set(state => ({ leftPanelOpen: !state.leftPanelOpen })),
        toggleRightPanel: () => set(state => ({ rightPanelOpen: !state.rightPanelOpen })),
        setActiveRightTab: (tab) => set({ activeRightTab: tab }),

        // Error handling
        addError: (error) => set(state => ({
          errors: [...state.errors, { ...error, id: uuidv4() }]
        })),
        clearErrors: () => set({ errors: [] })
      }),
      {
        name: 'website-builder-storage',
        partialize: (state) => ({
          viewport: state.viewport,
          zoom: state.zoom,
          leftPanelOpen: state.leftPanelOpen,
          rightPanelOpen: state.rightPanelOpen
        })
      }
    )
  )
);

